import React, { useEffect, useMemo, useRef, useState } from 'react';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import { Property } from '../App';

interface GoogleSatelliteMapViewProps {
  properties: Property[];
  onSelectProperty: (property: Property) => void;
  focusPropertyId?: string | null;
}

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function parseCoordinate(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value.trim());
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function getDisplayFullAddress(property: Property): string {
  const googleFormatted = property.googleFormattedAddress?.trim();
  if (googleFormatted) return googleFormatted;
  const full = property.fullAddress?.trim();
  if (full) return full;
  const addr = property.address?.trim();
  const city = property.city?.trim();
  const state = property.state?.trim();
  const zip = property.zipcode?.trim();
  const parts = [addr, [city, state, zip].filter(Boolean).join(' ').trim()].filter(
    (p) => typeof p === 'string' && p.trim().length > 0
  );
  return parts.length > 0 ? parts.join(', ') : 'Address not available';
}

const GoogleSatelliteMapView: React.FC<GoogleSatelliteMapViewProps> = ({ properties, onSelectProperty, focusPropertyId }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const searchMarkerRef = useRef<google.maps.Marker | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const hoverInfoRef = useRef<google.maps.InfoWindow | null>(null);
  const hasFittedRef = useRef(false);
  const onSelectPropertyRef = useRef(onSelectProperty);
  onSelectPropertyRef.current = onSelectProperty;
  const [errorMessage, setErrorMessage] = useState('');
  const [mapReady, setMapReady] = useState(false);

  const points = useMemo(() => {
    return properties
      .map((property) => {
        const latitude = parseCoordinate(property.latitude) ?? parseCoordinate(property.reportAllParcel?.latitude);
        const longitude = parseCoordinate(property.longitude) ?? parseCoordinate(property.reportAllParcel?.longitude);
        if (latitude === null || longitude === null) return null;
        return {
          property,
          lat: latitude,
          lng: longitude,
          title: property.tenant?.trim() || property.title?.trim() || 'Property',
          fullAddress: getDisplayFullAddress(property),
          city: property.city?.trim() || '',
          state: property.state?.trim() || '',
        };
      })
      .filter((point): point is NonNullable<typeof point> => point !== null);
  }, [properties]);

  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      setErrorMessage('Set REACT_APP_GOOGLE_MAPS_API_KEY to enable satellite view.');
      return;
    }

    let cancelled = false;
    (window as any).gm_authFailure = () => {
      setErrorMessage('Google auth failure. Check key restrictions, billing, and enabled APIs.');
    };
    setOptions({ key: apiKey, v: 'weekly' });

    const init = async () => {
      try {
        await Promise.all([importLibrary('maps'), importLibrary('places')]);
        if (cancelled || !containerRef.current) return;
        const map = new google.maps.Map(containerRef.current, {
          center: { lat: 39.8283, lng: -98.5795 },
          zoom: 3,
          mapTypeId: 'satellite',
          streetViewControl: false,
          fullscreenControl: false,
          mapTypeControl: true,
          mapTypeControlOptions: { mapTypeIds: ['roadmap', 'satellite', 'hybrid'] },
        });
        mapRef.current = map;
        infoWindowRef.current = new google.maps.InfoWindow();
        hoverInfoRef.current = new google.maps.InfoWindow({ disableAutoPan: true });

        if (searchInputRef.current) {
          const autocomplete = new google.maps.places.Autocomplete(searchInputRef.current, {
            fields: ['geometry', 'name', 'formatted_address'],
          });
          autocomplete.bindTo('bounds', map);
          autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            if (!place.geometry?.location) return;

            searchMarkerRef.current?.setMap(null);
            const marker = new google.maps.Marker({
              position: place.geometry.location,
              map,
              title: place.name || place.formatted_address || 'Search result',
              icon: {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 10,
                fillColor: '#3b82f6',
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 2.5,
              },
              zIndex: 2000,
            });
            searchMarkerRef.current = marker;

            if (place.geometry.viewport) {
              map.fitBounds(place.geometry.viewport);
            } else {
              map.panTo(place.geometry.location);
              map.setZoom(16);
            }
          });
        }

        setMapReady(true);
        setErrorMessage('');
      } catch (error: any) {
        setErrorMessage(error?.message || 'Failed to initialize Google Maps.');
      }
    };
    init();

    return () => {
      cancelled = true;
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];
      searchMarkerRef.current?.setMap(null);
      searchMarkerRef.current = null;
      mapRef.current = null;
      infoWindowRef.current = null;
      hoverInfoRef.current = null;
      setMapReady(false);
      delete (window as any).gm_authFailure;
    };
  }, []);

  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    if (points.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    const markers = points.map((point) => {
      const marker = new google.maps.Marker({
        position: { lat: point.lat, lng: point.lng },
        title: point.title,
        zIndex: 1000,
        map,
      });

      marker.addListener('click', () => {
        if (infoWindowRef.current) {
          infoWindowRef.current.setContent(`<strong>${escapeHtml(point.title)}</strong><br />${escapeHtml(point.fullAddress)}`);
          infoWindowRef.current.open({ map, anchor: marker });
        }
        onSelectPropertyRef.current(point.property);
      });

      marker.addListener('mouseover', () => {
        if (hoverInfoRef.current) {
          const content = `<div style="min-width:160px;line-height:1.4;color:#111827;font-size:13px;font-family:sans-serif;padding-top:2px;">
            <strong style="color:#111827;">${escapeHtml(point.title)}</strong><br/>
            ${escapeHtml(point.property.address?.trim() || '')}<br/>
            ${escapeHtml(point.city)}${point.city && point.state ? ', ' : ''}${escapeHtml(point.state)}
          </div>`;
          hoverInfoRef.current.setContent(content);
          hoverInfoRef.current.open({ map, anchor: marker });
        }
      });

      marker.addListener('mouseout', () => {
        hoverInfoRef.current?.close();
      });

      bounds.extend(marker.getPosition() as google.maps.LatLng);
      return marker;
    });

    markersRef.current = markers;

    if (!hasFittedRef.current) {
      map.fitBounds(bounds, 60);
      hasFittedRef.current = true;
    }
  }, [mapReady, points]);

  useEffect(() => {
    if (!mapReady || !focusPropertyId) return;
    const map = mapRef.current;
    if (!map) return;

    const point = points.find((p) => p.property.id === focusPropertyId);
    if (!point) return;

    map.panTo({ lat: point.lat, lng: point.lng });
    const currentZoom = map.getZoom() ?? 0;
    if (currentZoom < 15) map.setZoom(15);
  }, [mapReady, focusPropertyId, points]);

  const clearSearch = () => {
    if (searchInputRef.current) searchInputRef.current.value = '';
    searchMarkerRef.current?.setMap(null);
    searchMarkerRef.current = null;
  };

  return (
    <div className="google-map-shell">
      <div className="map-search-bar">
        <svg className="map-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          ref={searchInputRef}
          type="text"
          className="map-search-input"
          placeholder="Search locations..."
        />
        <button type="button" className="map-search-clear" onClick={clearSearch} title="Clear search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
      <div ref={containerRef} className="property-map-canvas" />
      {errorMessage ? (
        <div className="property-map-empty">
          <h3>Satellite unavailable</h3>
          <p>{errorMessage}</p>
        </div>
      ) : null}
    </div>
  );
};

export default GoogleSatelliteMapView;
